/**
 * ActionHistoryTimeline — per-action audit log, rendered as a vertical
 * timeline. Embedded in action detail dialogs so the user can see what
 * happened to *this* action without leaving for /audit.
 *
 * Sourced from the same /api/audit/actions endpoint the global Audit page
 * uses, scoped to one action_id. DESC ordering (newest first) matches the
 * Audit page convention.
 */
import { useActionAuditEvents } from '../../hooks/useDoneActions.js'
import { Bot, User, DollarSign } from 'lucide-react'
import { EVENT_META } from '../../utils/auditEvents.js'
import LoadingSpinner from './LoadingSpinner.jsx'

function fmtCurrency(v) {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return ''
  const sign = n < 0 ? '-' : ''
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`
  if (abs >= 1_000)     return `${sign}$${(abs / 1_000).toFixed(1)}K`
  return `${sign}$${abs.toFixed(0)}`
}

function fmtAbsoluteTs(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
  })
}

function fmtRelativeTs(iso) {
  if (!iso) return ''
  const ms = new Date(iso).getTime()
  const diffMs = Date.now() - ms
  const min = Math.round(diffMs / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.round(hr / 24)
  if (day < 30) return `${day}d ago`
  return ''
}

function summaryFor(ev) {
  if (ev.event_type === 'comment_added' && ev.comment) return ev.comment
  if (ev.event_type === 'value_set' && ev.value_usd != null) {
    return (
      <span className="inline-flex items-center gap-1 text-violet-200 tabular-nums">
        <DollarSign size={11} className="text-violet-400" />
        {fmtCurrency(ev.value_usd)}
      </span>
    )
  }
  // Engine events — concise field summary
  const parts = []
  if (ev.severity != null) parts.push(`sev ${ev.severity}`)
  if (ev.pnl_estimate != null) parts.push(fmtCurrency(ev.pnl_estimate))
  if (ev.urgency) parts.push(ev.urgency.replace(/[()]/g, '').toLowerCase())
  return parts.length ? parts.join(' · ') : null
}


/**
 * @param {object} props
 * @param {string} props.actionId — required
 * @param {boolean} [props.showHeader=true] — render the "History" label
 */
export default function ActionHistoryTimeline({ actionId, showHeader = true }) {
  const { data, isLoading, isError } = useActionAuditEvents(actionId)
  const events = data?.events ?? []

  return (
    <div>
      {showHeader && (
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
          History ({events.length})
        </div>
      )}

      {isLoading && (
        <div className="py-4 flex justify-center">
          <LoadingSpinner />
        </div>
      )}

      {isError && (
        <div className="text-xs text-red-400">Failed to load history.</div>
      )}

      {!isLoading && !isError && events.length === 0 && (
        <div className="text-xs text-slate-500">No history yet.</div>
      )}

      {!isLoading && !isError && events.length > 0 && (
        <ol className="relative">
          {/* Vertical rail */}
          <span
            aria-hidden="true"
            className="absolute left-[11px] top-2 bottom-2 w-px bg-white/10"
          />
          {events.map((ev, i) => (
            <TimelineEntry key={`${ev.ts}-${i}`} ev={ev} />
          ))}
        </ol>
      )}
    </div>
  )
}


function TimelineEntry({ ev }) {
  const meta = EVENT_META[ev.event_type] ?? {
    label: ev.event_type, icon: User,
    color: 'text-slate-400', bg: 'bg-white/5', border: 'border-white/10',
    source: ev.source,
  }
  const Icon = meta.icon
  const isSystem = ev.source === 'engine'
  const ActorIcon = isSystem ? Bot : User
  const actorLabel = isSystem ? 'system' : (ev.user_email || ev.user_name || '—')
  const summary = summaryFor(ev)

  return (
    <li className="relative pl-7 pb-3 last:pb-0">
      {/* Dot on the rail */}
      <span
        className={`absolute left-0 top-1 w-[22px] h-[22px] rounded-full border flex items-center justify-center ${meta.bg} ${meta.border}`}
      >
        <Icon size={11} className={meta.color} />
      </span>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-medium ${meta.color}`}>{meta.label}</span>
          <span className="text-[10px] text-slate-500">·</span>
          <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
            <ActorIcon size={10} />
            <span className={isSystem ? 'uppercase tracking-wider' : 'mp-mask'}>
              {actorLabel}
            </span>
          </span>
        </div>
        <div
          className="text-[10px] text-slate-500 tabular-nums whitespace-nowrap"
          title={ev.ts}
        >
          {fmtAbsoluteTs(ev.ts)}
          {fmtRelativeTs(ev.ts) && (
            <span className="ml-1 text-slate-600">({fmtRelativeTs(ev.ts)})</span>
          )}
        </div>
      </div>
      {summary && (
        <div className="mt-0.5 text-xs text-slate-300 whitespace-pre-wrap break-words">
          {summary}
        </div>
      )}
    </li>
  )
}
